<script setup lang="ts">
/**
 * Runtime shell for in-product announcements.
 *
 * Mounted once, app-wide, beside MicrolearningBlocker in app.js — not inside
 * AppLayout, which remounts on every Inertia navigation and would re-run the
 * open animation each time.
 *
 * Owns everything ambient: the Dialog (portal, focus trap, escape, scroll
 * lock), the Inertia prop read, and the network writes. The visuals live in
 * AnnouncementCard.vue, which the admin builder's preview renders with the
 * same props.
 */
import { computed, onUnmounted, ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import axios from '@/api/axios.js'
import Dialog from '@/shadcn/ui/dialog/Dialog.vue'
import DialogContent from '@/shadcn/ui/dialog/DialogContent.vue'
import DialogDescription from '@/shadcn/ui/dialog/DialogDescription.vue'
import DialogTitle from '@/shadcn/ui/dialog/DialogTitle.vue'
import AnnouncementCard from './AnnouncementCard.vue'
import { trackEvent } from '@/utils/analytics'
import type { ActiveAnnouncement } from '@/types/announcement'

/** Let the host page paint and settle before taking over the screen. */
const MOUNT_DELAY_MS = 600
/** Arrowing through five steps should be one write, not five. */
const STEP_FLUSH_MS = 400

const page = usePage()

const announcement = computed<ActiveAnnouncement | null>(
    () => (page.props.announcement as ActiveAnnouncement | null | undefined) ?? null,
)

const open = ref(false)
const stepIndex = ref(0)
/** Guards the view write so a re-render can't double-count. */
const recorded = ref<number | null>(null)

let openTimer: number | null = null
let stepTimer: number | null = null

const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const steps = computed(() => announcement.value?.steps ?? [])

/**
 * A mandatory announcement has no X, ignores Escape and the backdrop, and
 * ends only when the reader reaches the last step. Default is dismissible;
 * anything else is a real interruption and should be rare.
 */
const dismissible = computed(() => announcement.value?.dismissible !== false)

/**
 * Another overlay already owns the screen. Stacking on top of a Bootstrap
 * modal or the microlearning takeover would be hostile, and the announcement
 * has no deadline — skip silently and re-evaluate on the next navigation.
 * Nothing is recorded, so the user has not "seen" it.
 */
function screenIsBusy(): boolean {
    if (typeof document === 'undefined') return false
    if (page.props.microlearningBlock) return true
    if (document.body.classList.contains('modal-open')) return true
    return document.querySelector('[role="dialog"][data-state="open"]') !== null
}

/**
 * Admin tooling is never an announcement surface. The server agrees and
 * returns nothing for these routes, so this only catches a stale open racing a
 * navigation, which is exactly where announcing something at a super admin
 * mid-edit would be most absurd.
 */
function onAdminPage(): boolean {
    return typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')
}

function clearTimers(): void {
    if (openTimer !== null) {
        window.clearTimeout(openTimer)
        openTimer = null
    }
    if (stepTimer !== null) {
        window.clearTimeout(stepTimer)
        stepTimer = null
    }
}

function post(action: string, payload: Record<string, unknown> = {}): void {
    const id = announcement.value?.id
    if (!id) return
    // Best-effort telemetry: a failed write must never surface to the user or
    // block the modal they are reading.
    void axios
        .post(`/api/announcements/${id}/${action}`, payload, { suppressError: true } as never)
        .catch(() => {})
}

function flushStep(): void {
    if (stepTimer !== null) {
        window.clearTimeout(stepTimer)
        stepTimer = null
    }
    post('step', { step: stepIndex.value + 1 })
}

function onNext(): void {
    if (stepIndex.value >= steps.value.length - 1) return
    stepIndex.value += 1

    if (stepTimer !== null) window.clearTimeout(stepTimer)
    stepTimer = window.setTimeout(flushStep, STEP_FLUSH_MS)
}

function onBack(): void {
    if (stepIndex.value === 0) return
    stepIndex.value -= 1
}

/**
 * Reaching the end is a completion; closing early is a dismissal. Both close
 * the modal, and both are terminal — the announcement is already recorded as
 * seen, so it will not come back either way.
 */
function close(completed: boolean): void {
    if (!announcement.value) return

    flushStep()
    post(completed ? 'complete' : 'dismiss')
    trackEvent(completed ? 'Announcement Completed' : 'Announcement Dismissed', {
        announcement_id: announcement.value.id,
        surface: announcement.value.surface,
        step: stepIndex.value + 1,
    })

    open.value = false
}

/**
 * The button had somewhere to go. Record it as a completion first: the person
 * did the thing the announcement asked for, which is the strongest signal this
 * feature produces.
 */
function onCta(step: { cta_href?: string | null; cta_external?: boolean }): void {
    const href = step.cta_href
    if (!href) return

    close(true)

    if (step.cta_external) {
        window.open(href, '_blank', 'noopener')
        return
    }

    router.visit(href)
}

function onCardDismiss(): void {
    // The card's primary button emits `dismiss` on the last step, which is a
    // completion, not a bail-out.
    close(stepIndex.value >= steps.value.length - 1)
}

function onOpenChange(value: boolean): void {
    if (!value && open.value) {
        // Escape, the backdrop, or the X.
        if (!dismissible.value) return
        close(false)
        return
    }
    open.value = value
}

watch(
    announcement,
    (next) => {
        clearTimers()

        if (!next || next.steps.length === 0) {
            open.value = false
            return
        }

        if (recorded.value === next.id) return

        const show = () => {
            // Re-read rather than trusting the value captured 600ms ago: by
            // now the user may have navigated somewhere this announcement does
            // not belong, and opening it there would be both wrong and
            // unrecorded against the right surface.
            const current = announcement.value
            if (!current || current.id !== next.id) return
            if (onAdminPage() || screenIsBusy()) return

            recorded.value = next.id
            stepIndex.value = 0
            open.value = true

            // Recorded on actual render, not on prop resolution: a page that
            // errors before painting should not burn the user's one view.
            post('view', { surface: current.surface, step_count: current.steps.length })
        }

        if (reducedMotion) {
            show()
        } else {
            openTimer = window.setTimeout(show, MOUNT_DELAY_MS)
        }
    },
    { immediate: true },
)

// A navigation means any pending open is about the page being left, not the
// one being opened. Always drop it; the next page resolves its own.
const stopNavListener = router.on('start', () => clearTimers())

onUnmounted(() => {
    clearTimers()
    stopNavListener()
})
</script>

<template>
    <Dialog :open="open" @update:open="onOpenChange">
        <DialogContent
            v-if="announcement && steps.length"
            data-shadcn
            :hide-close="!dismissible"
            class="c-announcement-dialog z-[100100] gap-0 p-0 sm:max-w-[420px]"
            overlay-class="z-[100100]"
            @escape-key-down="(e: Event) => !dismissible && e.preventDefault()"
            @interact-outside="(e: Event) => !dismissible && e.preventDefault()"
            @pointer-down-outside="(e: Event) => !dismissible && e.preventDefault()"
        >
            <!-- reka requires both for aria labelling; the card renders the
                 visible copy, so these are the accessible names only. -->
            <DialogTitle class="sr-only">{{ steps[stepIndex]?.title }}</DialogTitle>
            <DialogDescription class="sr-only">
                {{ steps[stepIndex]?.body || 'Product announcement' }}
            </DialogDescription>

            <AnnouncementCard
                :steps="steps"
                :step-index="stepIndex"
                @next="onNext"
                @back="onBack"
                @dismiss="onCardDismiss"
                @cta="onCta"
            />
        </DialogContent>
    </Dialog>
</template>

<style lang="scss">
/**
 * Not scoped: the close control belongs to DialogContent, which renders
 * outside this component's style scope.
 *
 * It sits beside the card rather than on it, so it never fights a cover image
 * for contrast and never covers the copy. The card clips its own corners
 * (see AnnouncementCard), which is what lets this escape the dialog's edge.
 */
.c-announcement-dialog > button[type='button']:last-child {
    position: absolute;
    top: 0;
    right: -44px;
    left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    opacity: 1;
    transition: background-color 150ms ease;
}

.c-announcement-dialog > button[type='button']:last-child:hover {
    background: rgba(255, 255, 255, 0.24);
}

/**
 * Below roughly 520px the modal is nearly the width of the screen and there is
 * no room beside it, so the control comes back inside and takes a dark ground
 * to stay legible over a cover image.
 *
 * The dialog itself keeps a gutter rather than going edge to edge — a panel
 * that touches all four sides reads as a broken page rather than a deliberate
 * overlay, and the base only rounds its corners from `sm` up. The height
 * ceiling is the important part: without it a long announcement runs off the
 * bottom of the screen with its button on the far side of the fold.
 */
@media (max-width: 520px) {
    .c-announcement-dialog {
        width: calc(100vw - 24px);
        max-width: calc(100vw - 24px);
        max-height: calc(100vh - 24px);
        max-height: calc(100dvh - 24px);
        border-radius: 14px;
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    .c-announcement-dialog > button[type='button']:last-child {
        top: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
    }

    .c-announcement-dialog > button[type='button']:last-child:hover {
        background: rgba(0, 0, 0, 0.68);
    }
}

@media (prefers-reduced-motion: reduce) {
    .c-announcement-dialog > button[type='button']:last-child {
        transition: none;
    }
}
</style>
